import React from 'react';
import { Link } from 'react-router-dom';
import {
  StyledPostTitle,
  StyledParagraph,
  StyledSectionHeading,
  StyledOL,
} from '../styled';

const thStyle = {
  padding: '8px 12px 8px 0',
  borderBottom: '1px solid #000',
  verticalAlign: 'bottom',
};
const thLastStyle = { ...thStyle, padding: '8px 0' };
const rowHeadStyle = {
  padding: '10px 12px 10px 0',
  verticalAlign: 'top',
  fontWeight: 500,
};
const tdStyle = { padding: '10px 12px 10px 0', verticalAlign: 'top' };
const tdLastStyle = { padding: '10px 0', verticalAlign: 'top' };

const COMPARISON_ROWS = [
  ['Running since', '1997', 'Public beta since January 2023'],
  [
    'Size',
    'One very large world (about 200,000 managers in 2022)',
    'Small servers: 30 clubs each, split into three leagues of ten',
  ],
  ['Season length', '16 weeks', '3 weeks, then a 3-day off-season'],
  [
    'Match rhythm',
    'One league match each weekend, plus a midweek cup or friendly',
    'A league match every night at 7 pm server time, cup games at noon on most days',
  ],
  [
    'Where you follow matches',
    'On the Hattrick site',
    'Live text highlights in your league’s Discord channel',
  ],
  [
    'Transfers',
    'Bidding on a transfer list (three-day auctions)',
    'Agree a deal with the other manager, then send the offer in the portal',
  ],
  [
    'New talent',
    'Youth academy over many seasons',
    'Youth camp every off-season: scout, bid, sign',
  ],
  [
    'Tactics',
    'Deep, rewards years of planning',
    'Simple: formation, effort, mindset',
  ],
  [
    'Price',
    'Free, optional paid Supporter membership',
    'Free to play, optional paid Patron perks',
  ],
];

export default function Article() {
  return (
    <>
      <StyledPostTitle id="hattrick-alternative">
        Hattrick alternative: human leagues on a three-week clock
      </StyledPostTitle>
      <StyledParagraph big>
        Hattrick is the classic online football manager. It has run in the
        browser since 1997, its clubs are run by real people, and a season
        takes 16 weeks. People who search for a Hattrick alternative
        usually are not unhappy with the game. They want a different pace:
        results they can see this week, and a season they can finish this
        month.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League (TOL) keeps the part Hattrick fans care about most,
        which is human managers competing in a shared league. It runs on a much
        faster clock. Seasons last three weeks, league matches are played every
        night, and the league lives in Discord. It is also smaller and simpler
        than Hattrick, and this page is honest about that.
      </StyledParagraph>

      <StyledSectionHeading>
        What Hattrick does well, and why people still look elsewhere
      </StyledSectionHeading>
      <StyledParagraph big>
        Hattrick earned its reputation. Matches are against other managers, not
        just the computer. Players develop slowly through weekly training. And
        after almost 30 years, the community knows the game inside out.
      </StyledParagraph>
      <StyledParagraph big>
        The same strengths explain why some managers drift away:
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            The calendar is slow. With roughly two matches a week, you can wait
            days to find out whether a tactical change worked.
          </li>
          <li>
            Seasons are long. Sixteen weeks is a big commitment if you want to
            start fresh with a group of friends.
          </li>
          <li>
            The social side is on the site. If your friends already hang out in
            Discord, the league happens somewhere else.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        If none of that bothers you, keep playing Hattrick. If it does, read on.
      </StyledParagraph>

      <StyledSectionHeading>
        Hattrick vs The Open League at a glance
      </StyledSectionHeading>
      <StyledParagraph small>Facts as of October 2026.</StyledParagraph>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'Archivo, sans-serif',
            fontWeight: 300,
            fontSize: 16,
            lineHeight: '26px',
            letterSpacing: '0.4px',
            textAlign: 'left',
          }}
        >
          <thead>
            <tr>
              <th style={thStyle} aria-label="Comparison" />
              <th style={thStyle}>Hattrick</th>
              <th style={thLastStyle}>The Open League</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map(([label, hattrick, tol]) => (
              <tr key={label}>
                <th scope="row" style={rowHeadStyle}>
                  {label}
                </th>
                <td style={tdStyle}>{hattrick}</td>
                <td style={tdLastStyle}>{tol}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StyledSectionHeading>
        Where The Open League is the better fit
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            You want quick feedback. Set your starting eleven in the afternoon,
            follow the 7 pm match highlights in Discord, and adjust for
            tomorrow.
          </li>
          <li>
            You want seasons with an ending. Three weeks of league play,{' '}
            <Link to="/user-guide/competitions">
              the top three clubs go up, the bottom three go down
            </Link>
            , and then a weekend{' '}
            <Link to="/user-guide/off-season">youth camp</Link> before the next
            season starts.
          </li>
          <li>
            You want to know your rivals. Each server has 30 clubs, so you get
            to know the managers above and below you in the table.
          </li>
          <li>
            You like negotiating. Transfers start as a chat with another manager
            (“Sell me your striker for $5M?” “Make it 6.”), then you make it
            official in the club portal. Loans and split payments are common.
            TOL does not enforce side deals, so your word matters.
          </li>
          <li>
            Your group already lives in Discord. Match highlights, banter and
            deals all happen there. If you are bringing friends, see our guide
            to playing a{' '}
            <Link to="/blog/football-manager-with-friends">
              football manager with friends
            </Link>
            , and for how the league runs inside Discord, see{' '}
            <Link to="/blog/discord-football-manager">
              Discord football manager
            </Link>
            .
          </li>
          <li>
            You manage from your phone. Everything runs in the browser, so you
            can set your team from Android or iPhone. See{' '}
            <Link to="/blog/play-football-manager-on-phone">
              play football manager on phone
            </Link>
            .
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>
        Where Hattrick is still the better pick
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Long-term club building. If you enjoy planning training and finances
            across years, Hattrick’s slow pace is the point, not a flaw.
          </li>
          <li>
            Scale. Hattrick’s world and community are far larger than a 30-club
            TOL server. More leagues, more history, more guides.
          </li>
          <li>
            A low daily commitment. Two matches a week asks less of you than a
            match every night.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        Plenty of managers will be happiest playing both: a long Hattrick club
        in the background and a short TOL season with friends.
      </StyledParagraph>

      <StyledSectionHeading>
        Other games people try after Hattrick
      </StyledSectionHeading>
      <StyledParagraph big>
        TOL is not the only option, and it is not the fastest. A quick, fair
        look at a few others (based on their own sites, October 2026):
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Foot-Land: one match a day that you can watch and change live. The
            game interface is in French for now.
          </li>
          <li>
            ProMatch Lab: two fixtures a day, with a full league season in 11
            days.
          </li>
          <li>
            KickoffBoss: a long-running browser world with human leagues and a
            player-driven transfer market.
          </li>
          <li>
            Football Manager: the deepest single-player option, but paid and
            mostly solo. If that is the comparison you are making, read our{' '}
            <Link to="/blog/free-football-manager-alternative">
              free football manager alternative
            </Link>{' '}
            guide.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        What sets TOL apart is not raw speed. It is the combination of small
        servers, nightly matches streamed into Discord, and transfers you
        negotiate person to person.
      </StyledParagraph>

      <StyledSectionHeading>How to start a season tonight</StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Open <Link to="/">The Open League</Link> and register or log in.
          </li>
          <li>
            Pick a server whose 7 pm match time suits your evenings, and claim a
            club.
          </li>
          <li>
            Set your starting eleven and tactics, then join Discord to follow
            the match.
          </li>
          <li>
            Read <Link to="/user-guide/start">Getting started</Link> and the{' '}
            <Link to="/user-guide/trading">trading guide</Link> before your
            first deal.
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>FAQ</StyledSectionHeading>
      <StyledParagraph big>
        <strong>Is The Open League a Hattrick clone?</strong> No. Both are
        browser football managers where real people run the clubs, with
        promotion, relegation and transfers between managers. TOL plays at a
        much faster pace, keeps tactics simpler, and runs its league inside
        Discord.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>How long does a season take?</strong> Three weeks of nightly
        league matches, followed by a three-day off-season with a youth camp. A
        Hattrick season runs 16 weeks.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Is it free, like Hattrick?</strong> Yes, TOL is free to play.
        Like Hattrick’s Supporter membership, there is an optional paid tier.
        TOL’s Patron perks include an extra academy spot and automatic
        substitutions for injured players.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Can I move my Hattrick club over?</strong> No. There is no link
        between the two games. Everyone starts by claiming a club on a TOL
        server, and the first season is a good time to learn the transfer
        market.
      </StyledParagraph>

      <StyledParagraph big>
        A good Hattrick alternative should change what you actually want
        changed. For most people looking, that is the pace. The Open League
        keeps human rivals and real negotiations, and puts them on a three-week
        clock you can follow from Discord.
      </StyledParagraph>
      <StyledParagraph big>
        Want human rivals on a faster clock?{' '}
        <Link to="/">Claim a club in The Open League</Link> and follow tonight’s
        7 pm match in Discord.
      </StyledParagraph>

      <StyledParagraph small>
        Hattrick is a trademark of its owner. The Open League is not affiliated
        with Hattrick.
      </StyledParagraph>
    </>
  );
}
